/**
 * Wann darf ein fremder Tipp gezeigt werden?
 *
 * Antwort für alle Wettbewerbe: erst wenn er nicht mehr änderbar ist. Sonst
 * schreibt man vom Nachbarn ab. Die Sperren sind je Abschnitt verschieden —
 * Spieltag, Turnierphase, Sonderfrage — und lagen bisher als Kopien in den
 * jeweiligen Tipp-Komponenten. Hier stehen sie einmal, damit Tipp-Editor und
 * Tipp-Vorschau nicht auseinanderlaufen können.
 *
 * Grundhaltung überall: Was sich nicht sicher als gesperrt nachweisen lässt,
 * gilt als offen und wird damit verborgen.
 */

import type { CompetitionPhaseStatus, SpecialQuestion } from "../types/index.ts";

/** Nur die Felder, die für die Sperre zählen — nimmt LeagueFixture entgegen. */
type FixtureLike = { matchday: number; kickoff_utc: string };

/**
 * Der Zeitpunkt, zu dem ein Spieltag zumacht — in Millisekunden, oder null,
 * wenn er sich nicht bestimmen lässt.
 *
 * Maßgeblich ist matchday_schedule.first_kickoff_utc (Migration 026), dieselbe
 * Frist, die auch die RESTRICTIVE-RLS-Policy auf tips_match prüft. Der ganze
 * Spieltag sperrt zum Anpfiff des ersten Spiels, nicht je Einzelspiel.
 *
 * Geprüft wird mit `has`, nicht mit `??` oder Wahrheitswert: Für bereits
 * abgeschlossene Spieltage schreibt der League-Resolver epoch 0 hinein. Eine
 * 0 ist ein gültiger, längst vergangener Zeitpunkt — sie darf nicht als
 * "kein Eintrag" durchrutschen.
 *
 * Ohne Eintrag fällt es auf den frühesten Anpfiff der Spieltags-Fixtures
 * zurück. Das deckt sich mit dem "kein Eintrag → durchlässig"-Verhalten der
 * RLS-Policy.
 */
export function matchdayLockTimeMs(
  matchday: number,
  fixtures: readonly FixtureLike[],
  deadlines: ReadonlyMap<number, number>,
): number | null {
  if (deadlines.has(matchday)) return deadlines.get(matchday) as number;

  const kickoffs = fixtures
    .filter((f) => f.matchday === matchday)
    .map((f) => new Date(f.kickoff_utc).getTime())
    .filter((t) => !Number.isNaN(t));

  return kickoffs.length > 0 ? Math.min(...kickoffs) : null;
}

/**
 * Ist der Spieltag angepfiffen und damit gesperrt?
 *
 * Unbekannte Frist heißt nein — ein Spieltag, dessen Sperre wir nicht kennen,
 * gilt als offen und seine Tipps bleiben verborgen.
 */
export function isMatchdayLocked(
  matchday: number,
  fixtures: readonly FixtureLike[],
  deadlines: ReadonlyMap<number, number>,
  now: number = Date.now(),
): boolean {
  const lockTime = matchdayLockTimeMs(matchday, fixtures, deadlines);
  return lockTime != null && now >= lockTime;
}

/**
 * Darf eine Turnierphase (CL Ligaphase / K.o.) fremden Augen gezeigt werden?
 *
 * Nur "locked" und "resolved" — in beiden Zuständen ist der Tipp fix.
 * "open" ist offensichtlich tabu; "pending" ebenso, denn die Phase kann noch
 * aufgehen. Eine fehlende Phase (Ladefehler) gilt als unsichtbar, damit ein
 * Netzwerkfehler nicht versehentlich alles freigibt.
 */
export function isPhaseVisible(
  status: CompetitionPhaseStatus | null | undefined,
): boolean {
  return status === "locked" || status === "resolved";
}

/**
 * Ist die Antwortfrist einer Sonderfrage vorbei?
 *
 * Spiegelt die RESTRICTIVE-Policy sqa_deadline_restrictive (Migration 024),
 * die Antworten nur annimmt, solange status = 'open' und die Frist läuft.
 * Eine unlesbare Frist gilt als abgelaufen: Wir können nicht belegen, dass
 * noch geantwortet werden darf, also behandeln wir sie als geschlossen —
 * die Frage ist dann ohnehin nicht mehr beantwortbar.
 */
export function isSpecialQuestionLocked(
  question: Pick<SpecialQuestion, "status" | "answer_deadline">,
  now: number = Date.now(),
): boolean {
  if (question.status !== "open") return true;
  const deadline = new Date(question.answer_deadline).getTime();
  if (Number.isNaN(deadline)) return true;
  return now >= deadline;
}
